import { useState } from "react";
import { X, DeviceMobile, Check, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function PhoneVerifyDialog({ open, onClose }) {
  const { refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("+1");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/verify/start", { phone });
      toast.success("Code sent, check your texts.");
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send code");
    }
    setBusy(false);
  };

  const check = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/verify/check", { phone, code });
      await refresh();
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired code");
    }
    setBusy(false);
  };

  const close = () => { setStep(1); setPhone("+1"); setCode(""); onClose?.(); };

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm grid place-items-center p-4" onClick={close} data-testid="phone-verify-dialog">
      <div className="bg-cream rounded-2xl w-full max-w-md shadow-hero overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border flex items-start justify-between">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-2 flex items-center gap-1.5">
              <ShieldCheck size={12} weight="duotone" /> Verify phone
            </div>
            <h3 className="font-display text-2xl">
              {step === 3 ? "You're all set." : "Never miss a whisper."}
            </h3>
            <p className="text-sm text-ink/70 mt-1 leading-relaxed">
              {step === 1 && "We'll text a 6-digit code. No PHI, ever, just a heads-up when a new anonymous message arrives."}
              {step === 2 && `Enter the 6-digit code we sent to ${phone}.`}
              {step === 3 && "SMS pings are on for disappearing messages."}
            </p>
          </div>
          <button onClick={close} data-testid="phone-close" className="p-1.5 rounded-full hover:bg-muted"><X size={18} /></button>
        </div>

        {step === 1 && (
          <form onSubmit={start} className="p-6 space-y-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Phone (E.164)</label>
              <div className="mt-1 flex items-center gap-3 bg-white border border-border rounded-xl px-4 py-3 focus-within:border-coral focus-within:ring-2 focus-within:ring-coral/20">
                <DeviceMobile size={18} weight="duotone" className="text-sage" />
                <input
                  type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="+14155551234"
                  data-testid="phone-input"
                  className="flex-1 bg-transparent outline-none"
                />
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Include country code (US: +1). Message and data rates may apply.
              </div>
            </div>
            <button type="submit" disabled={busy} data-testid="phone-send-code"
              className="w-full bg-coral text-white py-3 rounded-full font-medium hover:bg-coral-600 disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
              {busy ? "Sending…" : "Send code"} {!busy && <ArrowRight size={16} />}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={check} className="p-6 space-y-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">6-digit code</label>
              <input
                type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} required
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                data-testid="phone-code-input"
                className="mt-1 w-full bg-white border border-border rounded-xl px-4 py-3 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none text-center text-2xl font-mono tracking-[0.4em]"
              />
            </div>
            <button type="submit" disabled={busy || code.length !== 6} data-testid="phone-verify-code"
              className="w-full bg-coral text-white py-3 rounded-full font-medium hover:bg-coral-600 disabled:opacity-50 transition-colors">
              {busy ? "Verifying…" : "Verify"}
            </button>
            <button type="button" onClick={() => setStep(1)} className="w-full text-sm text-muted-foreground hover:text-ink"
                    data-testid="phone-back">
              Wrong number? Go back
            </button>
          </form>
        )}

        {step === 3 && (
          <div className="p-6 space-y-4">
            <div className="bg-sage-50 border border-sage-100 rounded-xl p-5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-sage grid place-items-center shrink-0">
                <Check size={16} weight="bold" className="text-white" />
              </div>
              <div>
                <div className="font-display text-lg leading-tight">Verified.</div>
                <div className="text-xs text-muted-foreground">You'll get a discreet SMS when a peer messages you.</div>
              </div>
            </div>
            <button onClick={close} data-testid="phone-done"
              className="w-full bg-ink text-cream py-3 rounded-full hover:bg-coral transition-colors">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
